import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useId, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { ApiError, api, unwrap, type Schemas } from '../api/client.js';
import { errorMessage } from '../errors.js';
import { formatPrice, useLocale } from './site.js';
import {
  Button,
  Checkbox,
  LinkButton,
  LoadError,
  Loading,
  Notice,
  SelectField,
  TextField,
} from './ui.js';

/** "ab 199 €", "49 €", "ab 59 €/Monat" – net prices. */
export function servicePrice(
  s: Pick<Schemas['Service'], 'discountedPriceCents' | 'priceType'>,
  language: string,
  t: TFunction,
): string {
  return t(`price.${s.priceType}`, { price: formatPrice(s.discountedPriceCents, language) });
}

export interface ServiceRequestTarget {
  readonly siteId?: string | undefined;
  readonly findingId?: string | undefined;
  readonly incidentId?: string | undefined;
  readonly serviceCode?: string | undefined;
}

/** US-05: the service, its price, a message, how to reach the client and the consent. */
export function ServiceRequestForm({
  target,
  onDone,
}: {
  target: ServiceRequestTarget;
  onDone?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const locale = useLocale();
  const client = useQueryClient();
  const messageId = useId();
  const catalogue = useQuery({
    queryKey: ['services', locale],
    queryFn: () => unwrap(api.GET('/services', { params: { query: { locale } } })),
    staleTime: 60_000,
  });
  const [code, setCode] = useState(target.serviceCode ?? '');
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState<'email' | 'phone'>('email');
  const [phone, setPhone] = useState('');
  const [name, setName] = useState('');
  const [consent, setConsent] = useState(false);
  const [errors, setErrors] = useState<{ phone?: string; consent?: string }>({});
  const send = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST('/service-requests', {
          body: {
            ...(target.siteId ? { siteId: target.siteId } : {}),
            ...(target.findingId ? { findingId: target.findingId } : {}),
            ...(target.incidentId ? { incidentId: target.incidentId } : {}),
            ...(code ? { serviceCode: code } : {}),
            ...(message.trim() ? { message: message.trim() } : {}),
            ...(name.trim() ? { contactName: name.trim() } : {}),
            ...(contact === 'phone' ? { contactPhone: phone.trim() } : {}),
            preferredContact: contact,
            contactConsent: true,
          },
        }),
      ),
    onSuccess: () => client.invalidateQueries({ queryKey: ['requests'] }),
  });

  if (catalogue.isPending) return <Loading />;
  if (catalogue.isError)
    return (
      <LoadError
        message={errorMessage(catalogue.error, t)}
        onRetry={() => void catalogue.refetch()}
      />
    );
  if (send.isSuccess)
    return (
      <div className="flex flex-col gap-3">
        <Notice kind="success">
          <p className="font-semibold">{t('request.sent')}</p>
          <p>{t('request.sentText')}</p>
        </Notice>
        <p className="flex flex-wrap gap-3">
          <LinkButton to="/requests" variant="soft">
            {t('request.toList')}
          </LinkButton>
          {onDone ? (
            <Button variant="secondary" onClick={onDone}>
              {t('request.close')}
            </Button>
          ) : null}
        </p>
      </div>
    );

  const services = catalogue.data.items;
  const selected = services.find((s) => s.code === code);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    const next: typeof errors = {};
    if (contact === 'phone' && !/^\+?[0-9 ()/-]{6,30}$/.test(phone.trim()))
      next.phone = t('request.phoneInvalid');
    if (!consent) next.consent = t('request.consentRequired');
    setErrors(next);
    if (Object.keys(next).length === 0) send.mutate();
  };
  const failure =
    send.error instanceof ApiError && send.error.code === 'RATE_LIMITED'
      ? t('request.limit')
      : send.error
        ? errorMessage(send.error, t)
        : undefined;

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-4">
      {failure ? <Notice kind="error">{failure}</Notice> : null}
      <SelectField
        label={t('request.service')}
        value={code}
        onChange={(e) => setCode(e.target.value)}
      >
        <option value="">{t('request.general')}</option>
        {services.map((s) => (
          <option key={s.code} value={s.code}>
            {s.name} – {servicePrice(s, i18n.language, t)}
          </option>
        ))}
      </SelectField>
      {selected ? (
        <div className="rounded-md bg-slate-50 p-3">
          <p className="text-lg font-semibold">{servicePrice(selected, i18n.language, t)}</p>
          {selected.discountPct > 0 ? (
            <p className="text-sm text-green-800">
              {t('request.discount', {
                pct: selected.discountPct,
                list: formatPrice(selected.priceFromCents, i18n.language),
              })}
            </p>
          ) : null}
          <p className="mt-1 text-slate-800">{selected.description}</p>
          <p className="mt-1 text-sm text-slate-700">{t('request.net')}</p>
        </div>
      ) : null}
      <div className="flex flex-col gap-1">
        <label htmlFor={messageId} className="font-semibold">
          {t('request.message')}{' '}
          <span className="font-normal text-slate-600">({t('app.optional')})</span>
        </label>
        <textarea
          id={messageId}
          value={message}
          maxLength={4000}
          rows={4}
          onChange={(e) => setMessage(e.target.value)}
          className="rounded-md border border-slate-400 bg-white px-3 py-2 text-base"
        />
      </div>
      <fieldset className="flex flex-col gap-2">
        <legend className="mb-1 font-semibold">{t('request.contact')}</legend>
        {(['email', 'phone'] as const).map((c) => (
          <label key={c} className="flex min-h-11 items-center gap-3">
            <input
              type="radio"
              name="contact"
              value={c}
              checked={contact === c}
              onChange={() => setContact(c)}
              className="h-5 w-5 accent-blue-700"
            />
            {t(`request.contactBy.${c}`)}
          </label>
        ))}
      </fieldset>
      {contact === 'phone' ? (
        <TextField
          label={t('request.phone')}
          type="tel"
          autoComplete="tel"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          error={errors.phone}
        />
      ) : null}
      <TextField
        label={t('request.name')}
        optional
        autoComplete="name"
        value={name}
        onChange={(e) => setName(e.target.value)}
      />
      <Checkbox
        label={t('request.consent')}
        checked={consent}
        onChange={(e) => setConsent(e.target.checked)}
        error={errors.consent}
      />
      <div>
        <Button type="submit" busy={send.isPending}>
          {t('request.submit')}
        </Button>
      </div>
    </form>
  );
}
